import React, { useState } from "react";
import { auth, signInWithEmailAndPassword } from "config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useMutation, useApolloClient } from "@apollo/client";
import {
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import { StyledForm } from "./style";
import { SIGN_IN } from "graphql/users/mutations";
import { REPLACE_CART } from "graphql/quote/mutations";
import toast from "react-hot-toast";
import { safeJsonParse } from "utils";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [signIn, { loading: signInLoading, error: signInError }] =
    useMutation(SIGN_IN);
  const [
    replaceCart,
    { loading: replaceCartLoading, error: replaceCartError },
  ] = useMutation(REPLACE_CART);

  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState(null);
  const client = useApolloClient();

  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // 이메일 인증 확인
      if (!userCredential.user.emailVerified) {
        toast.error(
          "이메일 인증이 완료되지 않았습니다. 인증 메일을 확인해주세요.",
          { duration: 5000 }
        );
        navigate("/verify-email");
        return;
      }

      const userId = auth.currentUser.uid;
      const isLoginBtnClickedInQuoteCart = sessionStorage.getItem(
        "isLoginBtnClickedInQuoteCart"
      );
      if (isLoginBtnClickedInQuoteCart) {
        try {
          const orderList = safeJsonParse(sessionStorage.getItem("orderList"), []);
          const orders = orderList?.map((order) => ({
            productId: order.product.id,
            amount: order.amount,
          }));

          await replaceCart({
            variables: {
              id: userId,
              orders: orders,
            },
          });
          sessionStorage.removeItem("isLoginBtnClickedInQuoteCart");
        } catch (cartError) {
          console.error("Error replacing cart:", cartError);
          toast.error("장바구니 동기화 중 오류가 발생했습니다.", {
            duration: 3000,
          });
        }
      }

      const idToken = await userCredential.user.getIdToken();

      await signIn({
        variables: { idToken },
      });

      localStorage.setItem("authToken", idToken);

      toast.success("로그인에 성공했습니다!", { duration: 2000 });

      client.resetStore().then(() => {
        navigate("/");
      });
    } catch (error) {
      console.error("Error signing in", error);
      setAuthError(error);

      // Firebase 에러 메시지 처리
      if (error.code === "auth/user-not-found") {
        toast.error("등록되지 않은 이메일입니다.");
      } else if (error.code === "auth/wrong-password") {
        toast.error("비밀번호가 올바르지 않습니다.");
      } else if (error.code === "auth/invalid-email") {
        toast.error("유효하지 않은 이메일 형식입니다.");
      } else if (error.code === "auth/too-many-requests") {
        toast.error(
          "너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요."
        );
      } else if (error.code === "auth/network-request-failed") {
        toast.error("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      }
    }
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = () => {
    navigate("/reset-password");
  };
  return (
    <form onSubmit={handleSignIn}>
      <StyledForm>
        <Typography variant="subtitle" fontWeight="bold" className="highlight">
          로그인
        </Typography>
        <Box my={2}>
          <TextField
            type="email"
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => {
              setAuthError(null);
              setEmail(e.target.value);
            }}
            fullWidth
            margin="normal"
            required
            disabled={signInLoading || replaceCartLoading}
          />
          <TextField
            variant="outlined"
            type={showPassword ? "text" : "password"}
            label="Password"
            value={password}
            onChange={(e) => {
              setAuthError(null);
              setPassword(e.target.value);
            }}
            fullWidth
            margin="normal"
            required
            disabled={signInLoading || replaceCartLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword}>
                    {showPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={signInLoading || replaceCartLoading}
          sx={(theme) => ({
            width: "100%",
            color: "white",
            fontWeight: "bold",
            borderRadius: "2rem",
            paddingTop: "0.75rem",
            paddingBottom: "0.75rem",
          })}
        >
          {signInLoading || replaceCartLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <Typography>로그인</Typography>
          )}
        </Button>
        {(signInError || replaceCartError) && (
          <Typography variant="body" color="error">
            Error when signing in: {signInError.message}
            <br />
            Error when replacing cart: {replaceCartError.message}
          </Typography>
        )}
        {authError && (
          <>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="0.5rem"
            >
              <ErrorIcon color="error" />
              <Typography variant="body" color="error">
                이메일 또는 비밀번호가 맞지 않습니다. 다시 확인해 주세요.
              </Typography>
            </Box>
            <Box mt={2} className="signin-container">
              <Typography
                variant="body"
                sx={{
                  color: "gray",
                  marginRight: "0.5rem",
                }}
              >
                {"비밀번호를 잊으셨나요?"}
              </Typography>
              <Button onClick={handleForgotPassword} className="lower-button">
                <Typography variant="body">비밀번호 찾기</Typography>
              </Button>
            </Box>
          </>
        )}
      </StyledForm>
    </form>
  );
};

export default LoginForm;
